import React from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import AppIcon from '@atlaskit/icon/core/app';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	iconBadge: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.tile'),
		display: 'flex',
		height: '32px',
		justifyContent: 'center',
		width: '32px',
	},
});

type HubForgeEmptyStateProps = {
	description?: (typeof messages)[keyof typeof messages];
	title?: (typeof messages)[keyof typeof messages];
};

/**
 * Empty state shown inside the Hub Forge extension when no Forge renderer
 * has been injected for the slot — typically because the tenant has no
 * installed Forge app targeting `jsmHubPortalHeader` / `jsmHubPortalFooter`.
 *
 * Kept token-only (no custom CSS) so it inherits the editor's theme and
 * passes the platform Compiled-CSS lint rules without an `xcss` escape.
 */
export const HubForgeEmptyState = ({
	description = messages.emptyStateDescription,
	title = messages.emptyStateTitle,
}: HubForgeEmptyStateProps): React.JSX.Element => (
	<Box paddingBlock="space.500" paddingInline="space.300" testId="hub-forge-extension.empty-state">
		<Stack space="space.150" alignInline="center">
			<Box xcss={styles.iconBadge} testId="hub-forge-extension.empty-state.icon-tile">
				<AppIcon label="" color={token('color.icon.subtle')} />
			</Box>
			<Text as="strong" weight="bold">
				<FormattedMessage {...title} />
			</Text>
			<Text color="color.text.subtle" align="center">
				<FormattedMessage {...description} />
			</Text>
		</Stack>
	</Box>
);
